import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { logger } from 'shared/lib'
import {
  type IParamsWorkflowList,
  type IFullWorkflowData,
  type IResponseWorkflowListMeta,
  WorkflowsApi,
  WorkflowRow,
} from 'entities/Workflow'
import { tableLimitStore } from 'entities/TableLimit'

export class WorkflowListStore {
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _listMap = new Map<string, WorkflowRow>()

  readonly tableStore = new TableStore<WorkflowRow>({
    key: 'workflowListTable',
    limit: 10,
    page: 1,
    getLimit: () => tableLimitStore.getLimit('workflows-table'),
    setLimit: (value) => tableLimitStore.setLimit('workflows-table', value),
  })

  search = ''
  private _initialLoading = true

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      this.tableStore.setLoading(true)

      try {
        const {
          data: { payload, meta },
        } = await WorkflowsApi.getWorkflows(this.paramsGetItems, {
          cancelToken: cancelRequest.token,
        })
        this._setData(payload, meta)
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 408) {
          logger.warn(
            'TABLE: workflows-table Request timed out, likely due to large data volume. Consider adding more filters to reduce the dataset size.'
          )
          const newLimit = tableLimitStore.reduceLimit('workflows-table', this.tableStore.limit)
          this.tableStore.setLimit(newLimit)
        } else {
          throw error
        }
      }
    },
    finallyCallback: () => {
      runInAction(() => {
        this._initialLoading = false
        this.tableStore.setLoading(false)
      })
    },
  })

  constructor() {
    makeAutoObservable(this)

    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  get items() {
    return Array.from(this._listMap.values())
  }

  get initialLoading() {
    return this._initialLoading
  }

  get isEmpty() {
    if (this._initialLoading || this.tableStore.loading || this.search.length) return false

    return !this._listMap.size
  }

  get paramsWithoutPage(): Omit<IParamsWorkflowList, 'page[pointer]'> {
    return {
      'page[size]': this.tableStore.limit,
      'filters[name]': this.search || null,
    }
  }

  get paramsGetItems(): IParamsWorkflowList {
    return {
      ...this.paramsWithoutPage,
      'page[pointer]': this.tableStore.page,
    }
  }

  loadData = async () => {
    await this._requestLoadData.loadData()
  }

  onSuccessDelete = (id: string) => {
    this._listMap.delete(id)
    if (this._listMap.size === 0 && this.tableStore.page > 1) {
      this.tableStore.setPage(this.tableStore.page - 1)
    } else {
      this.loadData()
    }
  }

  setSearch = (search: string) => {
    this.search = search
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }

  private _setData = (list: IFullWorkflowData[], meta: IResponseWorkflowListMeta) => {
    this._listMap.clear()
    list.forEach((item) => this._listMap.set(item.id, new WorkflowRow(item)))
    this.tableStore.setTotal(meta.pagination.total)
  }
}
